import * as React from 'react';
import {Query} from 'react-apollo';

import {OverlappingAvatars} from 'shells/overlapping-avatars/overlapping-avatars';
import {Body} from 'shells/typography/body';

import type {
    GetTrialInfo as GetTrialInfoQuery,
    GetTrialInfo_sessionInstance as SessionInstance,
} from 'nutshell-graphql-types';

import {IntercomLaunchButton} from '../../intercom';

import GET_TRIAL_INFO from './graphql/queries/get-trial-info.graphql';

import styles from './chat-with-us.module.css';

const IMAGE_PATHS = {
    brett: '/include/images/portraits/brett_2x.jpg',
    james: '/include/images/portraits/james_2x.jpg',
    catarina: '/include/images/portraits/catarina.jpg',
} as const;

const PORTRAIT_SIZE = 19;

// Get a random order so that the front face isn't always the same
function getChatAgents() {
    const random = Math.floor(Math.random() * 3);

    return {
        '0': ['brett', 'james', 'catarina'],
        '1': ['james', 'catarina', 'brett'],
        '2': ['catarina', 'brett', 'james'],
    }[random];
}

function shouldAllowChatWithUs(sessionInstance?: SessionInstance | null) {
    return (
        sessionInstance &&
        sessionInstance.isDuringNutshellBusinessHours &&
        !sessionInstance.isChatHidden &&
        (sessionInstance.shouldShowChatCta ||
            window.location.pathname.includes('/setup/billing/sales'))
    );
}

class TrialInfoQuery extends Query<GetTrialInfoQuery, Record<any, any>> {}

export function ChatWithUs() {
    const [chatAgents, setChatAgents] = React.useState([]);

    React.useEffect(() => {
        // Doing this here so they don't change every time we re-render the component
        // @ts-expect-error
        setChatAgents(getChatAgents());
    }, []);

    // For trial instances we already show this component in the trial banner on the top of the page,
    // so we don't need to show it here as well.
    if (NutClientConfig.isTrial) {
        return null;
    }

    return (
        // @ts-expect-error
        <TrialInfoQuery query={GET_TRIAL_INFO} fetchPolicy='cache-and-network'>
            {({loading, data}) => {
                if (loading) return null;

                const sessionInstance = data ? data.sessionInstance : null;
                if (shouldAllowChatWithUs(sessionInstance)) {
                    return (
                        <div className={styles.chat}>
                            <span className={styles['active-now']} />
                            <OverlappingAvatars
                                avatars={chatAgents.map((agent) => {
                                    return {
                                        id: agent,
                                        type: 'users',
                                        name: agent,
                                        avatarUrl: IMAGE_PATHS[agent],
                                        initials: 'XX',
                                    };
                                })}
                                size={PORTRAIT_SIZE}
                            />
                            <div className={styles['have-questions']}>
                                <Body>Questions? Our team is online</Body>
                            </div>
                            <IntercomLaunchButton
                                size='big'
                                variant='text-primary'
                                noPadding={true}
                            >
                                Chat with us
                            </IntercomLaunchButton>
                        </div>
                    );
                }

                return null;
            }}
        </TrialInfoQuery>
    );
}
